import {
  type AnnulOperation,
  type BranchChangeset,
  isRegisteredTenantCreateOperation,
  type RegisteredTenantCreateOperation,
} from '@festina/configuration-general-types/configuration'
import { LayerKey } from '@festina-life/api'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'

import type { ConfigurationItemsByBlock } from '../../workspace/operationsContext'
import {
  findActiveAnnulments,
  indexAnnulmentOperations,
  indexChangesetOperations,
} from '../annulOperations/helpers'
import {
  ensureIndexedBlockLayerEntity,
  userActionsForOperation,
} from '../helpers'
import type { OperationWithSourceInfo, WorkspaceOperationOf } from '../types'
import type { IndexedRegisteredTenantCreateOperations } from './types'

export const constructIndexedRegisteredTenantCreateOperations = ({
  configurationChangesets,
  draftCreateOperations,
  draftAnnulOperations,
  rootBlock,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  draftCreateOperations: Optional<
    WorkspaceOperationOf<RegisteredTenantCreateOperation>[]
  >
  draftAnnulOperations: WorkspaceOperationOf<AnnulOperation>[]
  rootBlock: string
}): ConfigurationItemsByBlock<IndexedRegisteredTenantCreateOperations> => {
  const indexedAnnulments = indexAnnulmentOperations({
    changesets: configurationChangesets ?? [],
    baseBlock: rootBlock,
  })
  const indexedOperations = indexChangesetOperations(
    configurationChangesets ?? [],
  )
  const allOperations: OperationWithSourceInfo<RegisteredTenantCreateOperation>[] =
    (configurationChangesets ?? [])
      .flatMap((branchChangeset) =>
        (branchChangeset.changeset?.operations ?? [])
          .filter(notEmpty)
          .filter(isRegisteredTenantCreateOperation)
          .map(
            (
              operation,
            ): OperationWithSourceInfo<RegisteredTenantCreateOperation> => {
              const operationWithSourceInfo = {
                internalOperation: operation,
                isDraft: false,
                layer: new LayerKey({
                  tenantId: branchChangeset.changeset?.tenantId,
                  layerId: branchChangeset.changeset?.layer,
                }),
                changesetHistory: branchChangeset.changesetHistory,
                actions: userActionsForOperation(
                  operation,
                  branchChangeset.changesetHistory,
                ),
                block: branchChangeset.block ?? rootBlock,
              }

              const activeAnnulments = findActiveAnnulments({
                operation: operationWithSourceInfo,
                changesetAnnulments: indexedAnnulments,
                operationIndexes: indexedOperations,
                workspaceAnnulments: draftAnnulOperations,
              })

              return {
                ...operationWithSourceInfo,
                annulOperations: activeAnnulments,
              }
            },
          ),
      )
      .concat(
        (draftCreateOperations ?? []).map(
          (
            draftOperation,
          ): OperationWithSourceInfo<RegisteredTenantCreateOperation> => ({
            isDraft: true,
            layer: new LayerKey({
              tenantId: draftOperation.tenantId,
              layerId: draftOperation.layer,
            }),
            internalOperation: draftOperation.operation,
            changesetHistory: undefined,
            actions: [],
            block: draftOperation.block ?? rootBlock,
            annulOperations: [],
          }),
        ),
      )

  const indexedTenantOperations: ConfigurationItemsByBlock<IndexedRegisteredTenantCreateOperations> =
    new Map()

  allOperations.forEach((operation) => {
    const registeredTenantId =
      operation.internalOperation.registeredTenant?.tenantId
    assertNotEmpty(registeredTenantId)

    const indexedTenant = ensureIndexedBlockLayerEntity({
      allResults: indexedTenantOperations,
      block: operation.block,
      layer: operation.layer,
      entityId: registeredTenantId,
      createDefault: () => ({
        tenantId: registeredTenantId,
        isDraft: false,
        operations: [],
      }),
    })
    indexedTenant.operations.push(operation)
  })

  for (const blockLayers of indexedTenantOperations.values()) {
    for (const tenantEntries of blockLayers.values()) {
      for (const indexedTenant of tenantEntries.values()) {
        indexedTenant.isDraft = indexedTenant.operations.some(
          (operation) => operation.isDraft,
        )
      }
    }
  }

  return indexedTenantOperations
}
